// Relative, not '@/lib': the harness imports this file in node.
import { CLOSED_COLOR, STATE_CLOSED } from '../../../lib/winterSeason.js';
// The Trail map block's arithmetic, apart from the component so the
// harness can run it in node. See SiteTrailMap.vue.

// Same three colors the conditions block's dots use, so a trail is
// the same color in the list and on the map.
export const STATUS_LINE_COLORS = { open: '#2e9b4f', caution: '#d99a1e', closed: '#c2412d' };

export const MAP_LOOKS = [
    { key: 'trails', label: 'Trails' },
    { key: 'outdoor', label: 'Outdoor' },
    { key: 'minimal', label: 'Minimal' },
];

export function mapLook(config) {
    const k = config?.look;
    return MAP_LOOKS.some((l) => l.key === k) ? k : 'trails';
}

export function mapLineColor(trail, colorBy) {
    // Closed for the winter is grey whichever way the map is colored.
    if (trail?.season_state === STATE_CLOSED) return CLOSED_COLOR;
    if (colorBy === 'status') return STATUS_LINE_COLORS[trail?.bucket] || STATUS_LINE_COLORS.caution;
    const c = String(trail?.color || '');
    return /^#[0-9a-f]{3,8}$/i.test(c) ? c : '#2563eb';
}

/**
 * Where the block asks for its trails. On the app host (the /site/
 * preview and the editor, which runs at /settings/website) that is
 * /site/{org}/map/{id}.json; on the organization's own domain it is
 * /map/{id}.json, which ResolveCustomDomain dispatches. Same-origin
 * either way, which is what lets the basemap style it names be
 * same-origin too.
 */
export function siteMapUrl({ orgSlug, networkId, look = 'trails', canonical, hostPrefix }) {
    if (networkId === null || networkId === undefined || networkId === '') return null;
    const id = Number(networkId);
    if (!Number.isInteger(id) || id <= 0) return null;
    let base = '';
    if (canonical) {
        if (!orgSlug) return null;
        base = `/site/${encodeURIComponent(orgSlug)}`;
    } else if (typeof hostPrefix === 'string' && /^\/[a-z0-9-]+$/.test(hostPrefix)) {
        // The public host, go.trailsiq.com/{slug} — sitePrefix().
        base = hostPrefix;
    }
    return `${base}/map/${id}.json?look=${encodeURIComponent(look)}`;
}

/** The picked network if it is still a public one, else the first. */
export function pickNetwork(networks, id) {
    const list = (Array.isArray(networks) ? networks : []).filter((n) => n && n.id !== undefined && n.id !== null);
    if (id !== null && id !== undefined && id !== '') {
        const hit = list.find((n) => String(n.id) === String(id));
        if (hit) return hit;
    }
    return list[0] || null;
}

export function statusCounts(trails) {
    const out = { open: 0, caution: 0, closed: 0 };
    for (const t of trails || []) if (out[t.bucket] !== undefined) out[t.bucket]++;
    return out;
}

/**
 * The network drawn as an SVG, for an engine that does not get a live
 * map. Lines are [lng, lat]; longitude is scaled by cos(latitude) so a
 * trail running east is not drawn stretched, and the whole network is
 * fitted into an 800-wide box of the frame's own height.
 */
export function outlineGeometry(trails, height = 350, width = 800) {
    const list = (trails || []).filter((t) => Array.isArray(t.lines) && t.lines.length);
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const t of list) for (const line of t.lines) for (const p of line) {
        const [x, y] = p;
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    if (!Number.isFinite(minX)) return null;
    const pad = 32;
    const cos = Math.cos(((minY + maxY) / 2) * Math.PI / 180) || 1;
    const spanX = Math.max((maxX - minX) * cos, 1e-6);
    const spanY = Math.max(maxY - minY, 1e-6);
    const s = Math.min((width - pad * 2) / spanX, (height - pad * 2) / spanY);
    const offX = (width - spanX * s) / 2;
    const offY = (height - spanY * s) / 2;
    const pt = ([x, y]) => `${(offX + (x - minX) * cos * s).toFixed(1)},${(offY + (maxY - y) * s).toFixed(1)}`;
    return {
        viewBox: `0 0 ${width} ${height}`,
        trails: list.map((t) => ({
            ...t,
            paths: t.lines.filter((l) => l.length >= 2).map((l) => l.map(pt).join(' ')),
        })),
    };
}
